import { AppVisibilityMode } from '@electron/shared';
import { DEFAULT_DICTATION_SHORTCUT } from '@electron/shared/dictation.types';
import { isRegisterableAccelerator } from '@electron/shared/keyboard-accelerator';
import {
  DEFAULT_MEETING_WIDGET_SHORTCUT,
  type EventType,
} from '@electron/shared/meetings.types';
import {
  DEFAULT_SOUND_CHOICE,
  isNotificationSoundChoice,
} from '@electron/shared/sounds.types';
import {
  MEETING_ASSIST_CARD_KINDS,
  type MeetingAssistPopupRules,
} from '@littlebird/action-cards';
import { config, getSDK, hasSDK } from '@littlebird/sdk';
import {
  comparer,
  makeAutoObservable,
  reaction,
  runInAction,
  when,
} from 'mobx';

import {
  DEFAULT_RESPONSE_AUDIO_VOICE_ID,
  normalizeResponseAudioVoiceId,
} from '@/features/chat/voice/response-audio-voices';
import { electronApis } from '@/features/electron-api';
import { type ShowMeetingStatusWindowOption } from '@/features/electron-api/features/MeetingNotesAPI';
import {
  MAIN_WINDOW_ENTER_FULLSCREEN,
  MAIN_WINDOW_LEAVE_FULLSCREEN,
  WindowAPI,
} from '@/features/electron-api/features/WindowAPI';
import { normalizeMeetingAppBundleKey } from '@/features/meetings/meeting-app-allowlist';
import { normalizeMeetingSiteHosts } from '@/features/meetings/meeting-site-exclusions';
import {
  type MeetingMenubarIncludeEventsOption,
  type MeetingMenubarPreviewLeadTimeOption,
} from '@/features/meetings/meetings.types';
import {
  DEFAULT_VOICE_ACTIVITY_OPTIONS,
  type VoiceActivityDetectorOptions,
} from '@/features/speech-input/voice-activity-options';
import {
  normalizePreferredInputDevice,
  SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE,
  type PreferredInputDevice,
} from '@/lib/meetings/transcription-input-device';
import { inElectron } from '@/utils/capabilities';
import { DeviceSettingsStorage } from '@/utils/localStorage';

import { DEFAULT_NOTIFICATION_VOLUME } from '../../electron/shared/lb-notification.types';

export function parseBlob<T>(raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

const SETTINGS_DEVICE_KEY = 'littlebird_settings_v1';
const DEVICE_FIELD_KEY_PREFIX = `${SETTINGS_DEVICE_KEY}.`;

const deviceFieldKey = (field: keyof DeviceSettingsBlob) =>
  `${DEVICE_FIELD_KEY_PREFIX}${field}`;

export enum TypeScale {
  Small = '75%',
  Normal = '87.5%',
  Large = '100%',
  ExtraLarge = '112.5%',
}

export enum TypeScaleClass {
  Small = 'type-scale-sm',
  Normal = 'type-scale-normal',
  Large = 'type-scale-lg',
  ExtraLarge = 'type-scale-xl',
}

export const TYPE_SCALE_CLASSES = [
  TypeScaleClass.Small,
  TypeScaleClass.Normal,
  TypeScaleClass.Large,
  TypeScaleClass.ExtraLarge,
] as const;

const TYPE_SCALE_TO_CLASS: Record<TypeScale, TypeScaleClass> = {
  [TypeScale.Small]: TypeScaleClass.Small,
  [TypeScale.Normal]: TypeScaleClass.Normal,
  [TypeScale.Large]: TypeScaleClass.Large,
  [TypeScale.ExtraLarge]: TypeScaleClass.ExtraLarge,
};

type VoiceModeDetectionSettings = Pick<
  VoiceActivityDetectorOptions,
  | 'speechRmsThreshold'
  | 'minimumVoiceScore'
  | 'minimumVoiceFrequencyHz'
  | 'maximumVoiceFrequencyHz'
>;

export type MeetingAssistPopupSettings = MeetingAssistPopupRules & {
  readonly enabled: boolean;
  /** How long a popup stays up while the pointer is off it */
  readonly displaySeconds: number;
};

/** Allowed range of every numeric Meeting Assist popup setting; the settings UI reads these too */
export const MEETING_ASSIST_POPUP_RANGES = {
  minImportance: { min: 0, max: 10 },
  maxCardAgeSeconds: { min: 10, max: 3_600 },
  displaySeconds: { min: 3, max: 300 },
} as const;

// Internal users see every answer, so the team learns which ones are worth an interruption
const DEFAULT_MEETING_ASSIST_POPUP: MeetingAssistPopupSettings = {
  enabled: true,
  kinds: ['info'],
  minImportance: 0,
  maxCardAgeSeconds: 120,
  displaySeconds: 20,
};

type DeviceSettingsBlob = {
  typeScale: TypeScale;
  dyslexicFont: boolean;
  codeBlockLineWrap: boolean;
  timeFormat: boolean | null;
  appVisibilityMode: AppVisibilityMode;
  lbNotificationsEnabled: boolean;
  hummingbirdEnabled: boolean;
  hummingbirdShortcut: string;
  voiceShortcutEnabled: boolean;
  voiceAutoSendEnabled: boolean;
  hummingbirdFrontAppContextEnabled: boolean;
  hummingbirdScreenshotFallbackEnabled: boolean;
  hummingbirdHideFromScreenShare: boolean;
  dictationEnabled: boolean;
  dictationShortcut: string;
  meetingWidgetShortcut: string;
  dictationHoldShortcut: string;
  preferredDictationInputDevice: PreferredInputDevice;
  dictationAICleanupEnabled: boolean;
  preferredSpeechInputDeviceId: string | null;
  speechFeaturesEnabled: boolean;
  responseAudioVoiceId: string;
  voiceModePauseMs: number;
  voiceModeSubmittedSoundDelayMs: number;
  voiceModeMaxRecordingMs: number;
  voiceModeDetection: VoiceModeDetectionSettings;
  voiceModeThinkingSoundEnabled: boolean;
  preferredMeetingTranscriptionInputDevice: PreferredInputDevice;
  /** Internal-only: gates the live composer cost footer. When false, the
   * footer is unmounted AND the WS-seed REST call is never made. */
  costFooterEnabled: boolean;
  /** Internal-only: when false, this device shows what users who are not internal see,
   * without proactive agent chats or routines in the lists */
  internalProactiveAgentView: boolean;
  feedComposerChats: boolean;
  feedChatWorkingHoldMinutes: number;
  /** Internal-only: which Meeting Assist cards pop up beside the collapsed meeting widget */
  meetingAssistPopup: MeetingAssistPopupSettings;
  /** The agent whose cards home shows, or 'none'. Null follows My agents. */
  selectedProactiveAgent: string | null;
  /**
   * When true, the home screen also hides the cards that come from no proactive agent, such as cards
   * from the user's own chats and routines. Other agents' cards are hidden either way. The persisted key keeps the name
   * from the switch's old label, "Only this agent".
   */
  onlySelectedProactiveAgent: boolean;
  /** A new preference so existing devices start with only My agents visible */
  myAgentsOnly: boolean;
  sendDeviceTimezone: boolean;
  shortcutSettings: ShortcutSettings;
  disabledMeetingAppBundleKeys: string[];
  disabledMeetingSiteHosts: string[];
};

export type ShortcutSettings = Record<
  string,
  {
    disabled?: boolean;
    combo?: string;
  }
>;

const DEFAULTS = {
  device: {
    typeScale: TypeScale.Normal,
    dyslexicFont: false,
    codeBlockLineWrap: true,
    timeFormat: null as boolean | null,
    appVisibilityMode: AppVisibilityMode.DockAndStatusBar,
    lbNotificationsEnabled: true,
    hummingbirdEnabled: false,
    hummingbirdShortcut: 'option+option' as const,
    voiceShortcutEnabled: false,
    voiceAutoSendEnabled: true,
    hummingbirdFrontAppContextEnabled: true,
    hummingbirdScreenshotFallbackEnabled: false,
    hummingbirdHideFromScreenShare: false,
    dictationEnabled: false,
    dictationShortcut: DEFAULT_DICTATION_SHORTCUT,
    meetingWidgetShortcut: DEFAULT_MEETING_WIDGET_SHORTCUT,
    dictationHoldShortcut: '',
    preferredDictationInputDevice: SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE,
    dictationAICleanupEnabled: true,
    preferredSpeechInputDeviceId: null as string | null,
    speechFeaturesEnabled: true,
    responseAudioVoiceId: DEFAULT_RESPONSE_AUDIO_VOICE_ID,
    voiceModePauseMs: DEFAULT_VOICE_ACTIVITY_OPTIONS.trailingSilenceMs,
    voiceModeSubmittedSoundDelayMs: 1_000,
    voiceModeMaxRecordingMs: DEFAULT_VOICE_ACTIVITY_OPTIONS.maxRecordingMs,
    voiceModeDetection: {
      speechRmsThreshold: DEFAULT_VOICE_ACTIVITY_OPTIONS.speechRmsThreshold,
      minimumVoiceScore: DEFAULT_VOICE_ACTIVITY_OPTIONS.minimumVoiceScore,
      minimumVoiceFrequencyHz:
        DEFAULT_VOICE_ACTIVITY_OPTIONS.minimumVoiceFrequencyHz,
      maximumVoiceFrequencyHz:
        DEFAULT_VOICE_ACTIVITY_OPTIONS.maximumVoiceFrequencyHz,
    },
    voiceModeThinkingSoundEnabled: true,
    preferredMeetingTranscriptionInputDevice:
      SYSTEM_DEFAULT_PREFERRED_INPUT_DEVICE,
    costFooterEnabled: true,
    internalProactiveAgentView: true,
    feedComposerChats: true,
    feedChatWorkingHoldMinutes: 10,
    meetingAssistPopup: DEFAULT_MEETING_ASSIST_POPUP,
    selectedProactiveAgent: null,
    onlySelectedProactiveAgent: false,
    myAgentsOnly: true,
    sendDeviceTimezone: true,
    shortcutSettings: {} as ShortcutSettings,
    disabledMeetingAppBundleKeys: [] as string[],
    disabledMeetingSiteHosts: [] as string[],
  } satisfies DeviceSettingsBlob,
};

const TYPE_SCALE_Y: Record<TypeScale, number> = {
  [TypeScale.Small]: 10,
  [TypeScale.Normal]: 12,
  [TypeScale.Large]: 15,
  [TypeScale.ExtraLarge]: 18,
};

export const WINDOW_BUTTON_POSITION_X = 16;
const NAV_RAIL_TRAFFIC_LIGHT_OFFSET_Y = 4;

export const getWindowButtonPositionY = (typeScale: TypeScale): number =>
  TYPE_SCALE_Y[typeScale];

/** Allowed range of every numeric Voice Mode setting; the settings UI reads these too */
export const VOICE_MODE_RANGES = {
  pauseMs: { min: 500, max: 10_000 },
  submittedSoundDelayMs: { min: 0, max: 10_000 },
  maxRecordingMs: {
    min: 10_000,
    max: DEFAULT_VOICE_ACTIVITY_OPTIONS.maxRecordingMs,
  },
  speechRmsThreshold: { min: 0.001, max: 1 },
  minimumVoiceScore: { min: 0, max: 1 },
  voiceFrequencyHz: { min: 50, max: 8_000 },
} as const;

/** Restore the fallback for invalid input, otherwise clamp into the range */
function normalizeInRange(
  raw: unknown,
  range: { min: number; max: number },
  fallback: number
): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return fallback;
  return Math.min(Math.max(raw, range.min), range.max);
}

function normalizeMeetingAssistPopupSettings(
  raw: unknown
): MeetingAssistPopupSettings {
  const blob = (raw && typeof raw === 'object' ? raw : {}) as Partial<
    Record<keyof MeetingAssistPopupSettings, unknown>
  >;
  const defaults = DEFAULT_MEETING_ASSIST_POPUP;
  const storedKinds = blob.kinds;
  const inRange = (key: keyof typeof MEETING_ASSIST_POPUP_RANGES) =>
    Math.round(
      normalizeInRange(
        blob[key],
        MEETING_ASSIST_POPUP_RANGES[key],
        defaults[key]
      )
    );
  return {
    enabled:
      typeof blob.enabled === 'boolean' ? blob.enabled : defaults.enabled,
    kinds: Array.isArray(storedKinds)
      ? MEETING_ASSIST_CARD_KINDS.filter((kind) => storedKinds.includes(kind))
      : defaults.kinds,
    minImportance: inRange('minImportance'),
    maxCardAgeSeconds: inRange('maxCardAgeSeconds'),
    displaySeconds: inRange('displaySeconds'),
  };
}

function normalizeVoiceModePauseMs(raw: unknown): number {
  return Math.round(
    normalizeInRange(
      raw,
      VOICE_MODE_RANGES.pauseMs,
      DEFAULT_VOICE_ACTIVITY_OPTIONS.trailingSilenceMs
    )
  );
}

function normalizeVoiceModeSubmittedSoundDelayMs(raw: unknown): number {
  return Math.round(
    normalizeInRange(
      raw,
      VOICE_MODE_RANGES.submittedSoundDelayMs,
      DEFAULTS.device.voiceModeSubmittedSoundDelayMs
    )
  );
}

function normalizeSpeechFeaturesEnabled(raw: unknown): boolean {
  return raw === true;
}

function normalizeSpeechInputDeviceId(raw: unknown): string | null {
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

function normalizeHummingbirdFrontAppContextEnabled(raw: unknown): boolean {
  return raw !== false;
}

function normalizeVoiceModeMaxRecordingMs(raw: unknown): number {
  return Math.round(
    normalizeInRange(
      raw,
      VOICE_MODE_RANGES.maxRecordingMs,
      DEFAULT_VOICE_ACTIVITY_OPTIONS.maxRecordingMs
    )
  );
}

/** Restore defaults for invalid fields, clamp the rest, and keep the frequency band ordered */
function normalizeVoiceModeDetection(raw: unknown): VoiceModeDetectionSettings {
  const defaults = DEFAULTS.device.voiceModeDetection;
  const blob = (
    raw && typeof raw === 'object' ? raw : {}
  ) as Partial<VoiceModeDetectionSettings>;
  const next = {
    speechRmsThreshold: normalizeInRange(
      blob.speechRmsThreshold,
      VOICE_MODE_RANGES.speechRmsThreshold,
      defaults.speechRmsThreshold
    ),
    minimumVoiceScore: normalizeInRange(
      blob.minimumVoiceScore,
      VOICE_MODE_RANGES.minimumVoiceScore,
      defaults.minimumVoiceScore
    ),
    minimumVoiceFrequencyHz: normalizeInRange(
      blob.minimumVoiceFrequencyHz,
      VOICE_MODE_RANGES.voiceFrequencyHz,
      defaults.minimumVoiceFrequencyHz
    ),
    maximumVoiceFrequencyHz: normalizeInRange(
      blob.maximumVoiceFrequencyHz,
      VOICE_MODE_RANGES.voiceFrequencyHz,
      defaults.maximumVoiceFrequencyHz
    ),
  };
  if (next.minimumVoiceFrequencyHz >= next.maximumVoiceFrequencyHz) {
    next.minimumVoiceFrequencyHz = defaults.minimumVoiceFrequencyHz;
    next.maximumVoiceFrequencyHz = defaults.maximumVoiceFrequencyHz;
  }
  return next;
}

function normalizeShortcutSettings(raw: unknown): ShortcutSettings {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  return Object.fromEntries(
    Object.entries(raw).flatMap(([shortcutId, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return [];
      }

      const disabled = (value as { disabled?: unknown }).disabled === true;
      const rawCombo = (value as { combo?: unknown }).combo;
      const combo =
        typeof rawCombo === 'string' && rawCombo.trim().length > 0
          ? rawCombo.trim()
          : undefined;
      const nextValue = {
        ...(disabled ? { disabled: true } : {}),
        ...(combo ? { combo } : {}),
      };

      return Object.keys(nextValue).length > 0 ? [[shortcutId, nextValue]] : [];
    })
  );
}

function normalizeDisabledMeetingAppKeys(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];

  const normalizedKeys = raw
    .filter((key): key is string => typeof key === 'string')
    .map(normalizeMeetingAppBundleKey)
    .filter((key) => key.length > 0);

  return [...new Set(normalizedKeys)];
}

export class SettingsStore {
  // Device-local settings (persisted to localStorage; not synced).
  typeScale: TypeScale;
  dyslexicFont: boolean;
  codeBlockLineWrap: boolean;
  lbNotificationsEnabled: boolean;
  appVisibilityMode: AppVisibilityMode;
  hummingbirdEnabled: boolean;
  hummingbirdShortcut: string;
  voiceShortcutEnabled: boolean;
  voiceAutoSendEnabled: boolean;
  hummingbirdFrontAppContextEnabled: boolean;
  /** Attach a screenshot of a mentioned app when Hover cannot read its on-screen text */
  hummingbirdScreenshotFallbackEnabled: boolean;
  hummingbirdHideFromScreenShare: boolean;
  dictationEnabled: boolean;
  dictationShortcut: string;
  meetingWidgetShortcut: string;
  dictationHoldShortcut: string;
  preferredDictationInputDevice: PreferredInputDevice;
  dictationAICleanupEnabled: boolean;
  /** Chromium device id used by chat dictation, Voice Mode, and other browser speech capture */
  preferredSpeechInputDeviceId: string | null;
  speechFeaturesEnabled: boolean; // Device preference; account eligibility is checked separately
  responseAudioVoiceId: string; // Voice used for Voice Mode and Read Aloud
  voiceModePauseMs: number; // Silence before automatic submission, in milliseconds
  voiceModeSubmittedSoundDelayMs: number; // Maximum wait before the submission sound, in milliseconds
  voiceModeMaxRecordingMs: number; // Maximum recording length, in milliseconds
  voiceModeDetection: VoiceModeDetectionSettings;
  voiceModeThinkingSoundEnabled: boolean;
  timeFormat: boolean | null;
  preferredMeetingTranscriptionInputDevice: PreferredInputDevice;
  costFooterEnabled: boolean;
  internalProactiveAgentView: boolean;
  /** The proactive agent whose action cards the home screen shows, or 'none'. Null follows the default agent. */
  selectedProactiveAgent: string | null;
  /** When true, the home switch Hide your own cards is on (see `DeviceSettingsBlob`) */
  onlySelectedProactiveAgent: boolean;
  /** When true, the Feed hides cards from agents outside My agents */
  myAgentsOnly: boolean;
  sendDeviceTimezone: boolean;
  shortcutSettings: ShortcutSettings;
  disabledMeetingAppBundleKeys: string[];
  disabledMeetingSiteHosts: string[];
  feedComposerChats: boolean;
  feedChatWorkingHoldMinutes: number;
  meetingAssistPopup: MeetingAssistPopupSettings;
  isMainWindowFullScreen = false;
  isApplyingFromStorage = false;
  private disposers: Array<() => void> = [];
  private stopSideEffects: (() => void) | null = null;

  constructor() {
    const device = { ...DEFAULTS.device, ...this.hydrateDeviceBlob() };

    this.typeScale = device.typeScale;
    this.dyslexicFont = device.dyslexicFont;
    this.codeBlockLineWrap = device.codeBlockLineWrap;
    this.timeFormat = device.timeFormat;
    this.appVisibilityMode = device.appVisibilityMode;
    this.lbNotificationsEnabled = device.lbNotificationsEnabled;
    this.hummingbirdEnabled = device.hummingbirdEnabled;
    this.hummingbirdShortcut = device.hummingbirdShortcut;
    this.voiceShortcutEnabled = device.voiceShortcutEnabled;
    this.voiceAutoSendEnabled = device.voiceAutoSendEnabled;
    this.hummingbirdFrontAppContextEnabled =
      normalizeHummingbirdFrontAppContextEnabled(
        device.hummingbirdFrontAppContextEnabled
      );
    this.hummingbirdScreenshotFallbackEnabled =
      device.hummingbirdScreenshotFallbackEnabled === true;
    this.hummingbirdHideFromScreenShare = device.hummingbirdHideFromScreenShare;
    this.dictationEnabled = device.dictationEnabled;
    this.dictationShortcut = device.dictationShortcut;
    this.meetingWidgetShortcut = device.meetingWidgetShortcut;
    this.dictationHoldShortcut = device.dictationHoldShortcut;
    this.preferredDictationInputDevice = normalizePreferredInputDevice(
      device.preferredDictationInputDevice
    );
    this.dictationAICleanupEnabled = device.dictationAICleanupEnabled;
    this.preferredSpeechInputDeviceId = normalizeSpeechInputDeviceId(
      device.preferredSpeechInputDeviceId
    );
    this.speechFeaturesEnabled = normalizeSpeechFeaturesEnabled(
      device.speechFeaturesEnabled
    );
    this.responseAudioVoiceId = normalizeResponseAudioVoiceId(
      device.responseAudioVoiceId
    );
    this.voiceModePauseMs = normalizeVoiceModePauseMs(device.voiceModePauseMs);
    this.voiceModeSubmittedSoundDelayMs =
      normalizeVoiceModeSubmittedSoundDelayMs(
        device.voiceModeSubmittedSoundDelayMs
      );
    this.voiceModeMaxRecordingMs = normalizeVoiceModeMaxRecordingMs(
      device.voiceModeMaxRecordingMs
    );
    this.voiceModeDetection = normalizeVoiceModeDetection(
      device.voiceModeDetection
    );
    this.voiceModeThinkingSoundEnabled = device.voiceModeThinkingSoundEnabled;
    this.preferredMeetingTranscriptionInputDevice =
      normalizePreferredInputDevice(
        device.preferredMeetingTranscriptionInputDevice
      );
    this.costFooterEnabled = device.costFooterEnabled;
    this.internalProactiveAgentView = device.internalProactiveAgentView;
    this.feedComposerChats = device.feedComposerChats;
    this.feedChatWorkingHoldMinutes = device.feedChatWorkingHoldMinutes;
    this.meetingAssistPopup = normalizeMeetingAssistPopupSettings(
      device.meetingAssistPopup
    );
    this.selectedProactiveAgent = device.selectedProactiveAgent;
    this.onlySelectedProactiveAgent = device.onlySelectedProactiveAgent;
    this.myAgentsOnly = device.myAgentsOnly;
    this.sendDeviceTimezone = device.sendDeviceTimezone;
    this.shortcutSettings = normalizeShortcutSettings(device.shortcutSettings);
    this.disabledMeetingAppBundleKeys = normalizeDisabledMeetingAppKeys(
      device.disabledMeetingAppBundleKeys
    );
    this.disabledMeetingSiteHosts = normalizeMeetingSiteHosts(
      device.disabledMeetingSiteHosts
    );

    makeAutoObservable(this, {
      hydrateDeviceBlob: false,
      isApplyingFromStorage: false,
      persistDeviceField: false,
      initElectronReactions: false,
      initDocumentFont: false,
      initCrossWindowSync: false,
      initFromElectron: false,
      hydrateFromElectron: false,
      react: false,
      reactToUserConfig: false,
      start: false,
      disposers: false,
      stopSideEffects: false,
    } as Parameters<typeof makeAutoObservable>[1]);
  }

  /**
   * Starts persistence, the IPC pushes, the document font, cross-window sync
   * and the hydration from the main process. The app calls this once at
   * bootstrap. Returns a function that stops all of it.
   */
  start(): () => void {
    if (this.stopSideEffects) return this.stopSideEffects;

    // Coalesce to explicit booleans so partial/first-run blobs don't fail schema validation.
    electronApis.meetings?.logAutoStartEvent?.('settings_loaded', {
      meeting_auto_start_on_detection: config.meetings.auto_start_on_detection,
      meeting_disable_detection_notification:
        config.meetings.disable_detection_notification,
    });

    // This run's own disposers; a disposer from an earlier run must not touch them
    const disposers: Array<() => void> = [];
    this.disposers = disposers;
    this.initPersistence();
    this.initElectronReactions();
    this.initDocumentFont();
    this.initCrossWindowSync();
    this.initFromElectron();

    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      for (const dispose of disposers) dispose();
      if (this.stopSideEffects === stop) this.stopSideEffects = null;
    };
    this.stopSideEffects = stop;
    return stop;
  }

  private react: typeof reaction = (...args) => {
    const dispose = reaction(...(args as Parameters<typeof reaction>));
    this.disposers.push(dispose);
    return dispose;
  };

  private hydrateDeviceBlob(): DeviceSettingsBlob {
    const legacyBlob = parseBlob(
      DeviceSettingsStorage.getItem(SETTINGS_DEVICE_KEY) ??
        localStorage.getItem(SETTINGS_DEVICE_KEY),
      {} as Partial<DeviceSettingsBlob>
    );
    const fields = Object.keys(DEFAULTS.device) as Array<
      keyof DeviceSettingsBlob
    >;
    const storedFields = fields.flatMap((field) => {
      const value = parseBlob<unknown>(
        DeviceSettingsStorage.getItem(deviceFieldKey(field)),
        undefined
      );
      return value === undefined ? [] : [[field, value] as const];
    });
    return {
      ...DEFAULTS.device,
      ...legacyBlob,
      ...(Object.fromEntries(storedFields) as Partial<DeviceSettingsBlob>),
    };
  }

  get typeScaleClass(): TypeScaleClass {
    return TYPE_SCALE_TO_CLASS[this.typeScale] ?? TypeScaleClass.Normal;
  }

  setTypeScale(value: TypeScale) {
    this.typeScale = value;
  }
  setDyslexicFont(value: boolean) {
    this.dyslexicFont = value;
  }
  setCodeBlockLineWrap(value: boolean) {
    this.codeBlockLineWrap = value;
  }
  setLbNotificationsEnabled(value: boolean) {
    this.lbNotificationsEnabled = value;
  }
  setAppVisibilityMode(value: AppVisibilityMode) {
    this.appVisibilityMode = value;
  }
  setHummingbirdEnabled(value: boolean) {
    this.hummingbirdEnabled = value;
  }
  setHummingbirdShortcut(value: string) {
    this.hummingbirdShortcut = value;
  }
  setVoiceShortcutEnabled(value: boolean) {
    this.voiceShortcutEnabled = value;
  }
  setVoiceAutoSendEnabled(value: boolean) {
    this.voiceAutoSendEnabled = value;
  }
  setHummingbirdFrontAppContextEnabled(value: boolean) {
    this.hummingbirdFrontAppContextEnabled = value;
  }
  setHummingbirdScreenshotFallbackEnabled(value: boolean) {
    this.hummingbirdScreenshotFallbackEnabled = value;
  }
  setHummingbirdHideFromScreenShare(value: boolean) {
    this.hummingbirdHideFromScreenShare = value;
  }
  setDictationEnabled(value: boolean) {
    this.dictationEnabled = value;
  }
  setDictationHoldShortcut(value: string) {
    this.dictationHoldShortcut = value;
  }

  setDictationShortcut(value: string) {
    this.dictationShortcut = value;
  }
  setMeetingWidgetShortcut(value: string) {
    this.meetingWidgetShortcut = value;
  }
  setPreferredDictationInputDevice(value: PreferredInputDevice) {
    this.preferredDictationInputDevice = value;
  }
  setDictationAICleanupEnabled(value: boolean) {
    this.dictationAICleanupEnabled = value;
  }
  setPreferredSpeechInputDeviceId(value: string | null) {
    this.preferredSpeechInputDeviceId = normalizeSpeechInputDeviceId(value);
  }
  setSpeechFeaturesEnabled(value: boolean) {
    this.speechFeaturesEnabled = value;
  }
  setResponseAudioVoiceId(value: string) {
    this.responseAudioVoiceId = normalizeResponseAudioVoiceId(value);
  }
  setVoiceModePauseMs(value: number) {
    this.voiceModePauseMs = normalizeVoiceModePauseMs(value);
  }
  setVoiceModeSubmittedSoundDelayMs(value: number) {
    this.voiceModeSubmittedSoundDelayMs =
      normalizeVoiceModeSubmittedSoundDelayMs(value);
  }
  setVoiceModeMaxRecordingMs(value: number) {
    this.voiceModeMaxRecordingMs = normalizeVoiceModeMaxRecordingMs(value);
  }
  setVoiceModeDetection(update: Partial<VoiceModeDetectionSettings>) {
    this.voiceModeDetection = normalizeVoiceModeDetection({
      ...this.voiceModeDetection,
      ...update,
    });
  }
  setVoiceModeThinkingSoundEnabled(value: boolean) {
    this.voiceModeThinkingSoundEnabled = value;
  }
  setPreferredMeetingTranscriptionInputDevice(value: PreferredInputDevice) {
    this.preferredMeetingTranscriptionInputDevice = value;
  }
  setTimeFormat(value: boolean | null) {
    this.timeFormat = value;
  }
  setCostFooterEnabled(value: boolean) {
    this.costFooterEnabled = value;
  }
  setInternalProactiveAgentView(value: boolean) {
    this.internalProactiveAgentView = value;
  }
  setFeedComposerChats(value: boolean) {
    this.feedComposerChats = value;
  }
  setFeedChatWorkingHoldMinutes(value: number) {
    this.feedChatWorkingHoldMinutes = value;
  }
  setMeetingAssistPopup(patch: Partial<MeetingAssistPopupSettings>) {
    this.meetingAssistPopup = normalizeMeetingAssistPopupSettings({
      ...this.meetingAssistPopup,
      ...patch,
    });
  }
  setSelectedProactiveAgent(value: string | null) {
    this.selectedProactiveAgent = value;
  }
  setOnlySelectedProactiveAgent(value: boolean) {
    this.onlySelectedProactiveAgent = value;
  }
  setMyAgentsOnly(value: boolean) {
    this.myAgentsOnly = value;
  }
  setSendDeviceTimezone(value: boolean) {
    this.sendDeviceTimezone = value;
  }
  setShortcutEnabled(shortcutId: string, enabled: boolean) {
    const current = this.shortcutSettings[shortcutId] ?? {};
    const next = { ...this.shortcutSettings };

    if (enabled) {
      const { disabled: _disabled, ...rest } = current;
      if (Object.keys(rest).length > 0) {
        next[shortcutId] = rest;
      } else {
        delete next[shortcutId];
      }
    } else {
      next[shortcutId] = { ...current, disabled: true };
    }

    this.shortcutSettings = next;
  }
  isShortcutEnabled(shortcutId: string): boolean {
    return this.shortcutSettings[shortcutId]?.disabled !== true;
  }
  setShortcutCombo(shortcutId: string, combo: string | undefined) {
    const current = this.shortcutSettings[shortcutId] ?? {};
    const next = { ...this.shortcutSettings };
    const normalizedCombo = combo?.trim();

    if (normalizedCombo) {
      next[shortcutId] = { ...current, combo: normalizedCombo };
    } else {
      const { combo: _combo, ...rest } = current;
      if (Object.keys(rest).length > 0) {
        next[shortcutId] = rest;
      } else {
        delete next[shortcutId];
      }
    }

    this.shortcutSettings = next;
  }
  getShortcutCombo(shortcutId: string): string | undefined {
    return this.shortcutSettings[shortcutId]?.combo;
  }
  resetShortcutSettings() {
    this.shortcutSettings = {};
  }
  setIsMainWindowFullScreen(value: boolean) {
    this.isMainWindowFullScreen = value;
  }
  setMeetingAppGroupDisabled(keys: string[], disabled: boolean) {
    const next = new Set(this.disabledMeetingAppBundleKeys);
    for (const key of keys) {
      if (disabled) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    this.disabledMeetingAppBundleKeys = [...next];
  }
  clearDisabledMeetingAppKeys() {
    this.disabledMeetingAppBundleKeys = [];
  }
  addDisabledMeetingSiteHost(host: string) {
    this.disabledMeetingSiteHosts = normalizeMeetingSiteHosts([
      ...this.disabledMeetingSiteHosts,
      host,
    ]);
  }
  removeDisabledMeetingSiteHost(host: string) {
    this.disabledMeetingSiteHosts = this.disabledMeetingSiteHosts.filter(
      (existingHost) => existingHost !== host
    );
  }

  private initPersistence() {
    // Per-field reactions so writing one setting doesn't overwrite the others
    for (const key of Object.keys(DEFAULTS.device) as Array<
      keyof DeviceSettingsBlob
    >) {
      this.persistDeviceField(key, () => this[key]);
    }
  }

  private persistDeviceField<K extends keyof DeviceSettingsBlob>(
    key: K,
    read: () => DeviceSettingsBlob[K]
  ) {
    this.react(read, (value) => {
      if (this.isApplyingFromStorage) return;
      DeviceSettingsStorage.setItem(deviceFieldKey(key), JSON.stringify(value));
    });
  }

  private initDocumentFont() {
    this.react(
      () => this.dyslexicFont,
      (enabled) =>
        document.documentElement.toggleAttribute('data-dyslexic-font', enabled),
      { fireImmediately: true }
    );
  }

  /** Pushes settings mutations from any same-origin window to this store via the localStorage `storage` event. */
  private initCrossWindowSync() {
    if (!inElectron()) return;

    const applyStorageEvent = (event: StorageEvent) => {
      const fieldKeyPrefix = `ds:${DEVICE_FIELD_KEY_PREFIX}`;
      if (!event.key?.startsWith(fieldKeyPrefix)) return;
      if (!event.newValue) return;

      let value: unknown;
      try {
        value = JSON.parse(event.newValue);
      } catch {
        return;
      }

      const blob = {
        [event.key.slice(fieldKeyPrefix.length)]: value,
      } as Partial<DeviceSettingsBlob>;

      this.isApplyingFromStorage = true;
      try {
        runInAction(() => {
          const store = this as SettingsStore;
          const applyField = <
            K extends keyof DeviceSettingsBlob & keyof SettingsStore,
          >(
            key: K,
            normalize?: (raw: unknown) => SettingsStore[K]
          ) => {
            if (!(key in blob)) return;
            const value = blob[key];
            store[key] = normalize
              ? normalize(value)
              : (value as SettingsStore[K]);
          };

          const meetingWidgetShortcut: unknown = blob.meetingWidgetShortcut;
          if (
            typeof meetingWidgetShortcut === 'string' &&
            (meetingWidgetShortcut === '' ||
              isRegisterableAccelerator(meetingWidgetShortcut))
          ) {
            store.meetingWidgetShortcut = meetingWidgetShortcut;
          }

          applyField(
            'preferredMeetingTranscriptionInputDevice',
            normalizePreferredInputDevice
          );
          applyField(
            'preferredSpeechInputDeviceId',
            normalizeSpeechInputDeviceId
          );
          // Older app versions omit this field, leaving the current value
          // unchanged. For a present value, accept only literal `true`; false
          // or malformed values disable speech features, and the Voice Mode
          // runtime ends an active session
          applyField('speechFeaturesEnabled', normalizeSpeechFeaturesEnabled);
          applyField('responseAudioVoiceId', normalizeResponseAudioVoiceId);
          applyField('voiceModePauseMs', normalizeVoiceModePauseMs);
          applyField(
            'voiceModeSubmittedSoundDelayMs',
            normalizeVoiceModeSubmittedSoundDelayMs
          );
          applyField(
            'voiceModeMaxRecordingMs',
            normalizeVoiceModeMaxRecordingMs
          );
          applyField('voiceModeDetection', normalizeVoiceModeDetection);
          applyField('voiceModeThinkingSoundEnabled');
          applyField('dyslexicFont');
          applyField('shortcutSettings', normalizeShortcutSettings);
          applyField('internalProactiveAgentView');
          applyField('feedComposerChats');
          applyField('feedChatWorkingHoldMinutes');
          // The meeting widget is its own window, so it only learns about
          // these settings through this event
          applyField('meetingAssistPopup', normalizeMeetingAssistPopupSettings);
          applyField('selectedProactiveAgent');
          applyField('onlySelectedProactiveAgent');
          applyField('myAgentsOnly');
          applyField(
            'disabledMeetingAppBundleKeys',
            normalizeDisabledMeetingAppKeys
          );
          applyField('disabledMeetingSiteHosts', normalizeMeetingSiteHosts);
          applyField(
            'hummingbirdFrontAppContextEnabled',
            normalizeHummingbirdFrontAppContextEnabled
          );
          // The Hover overlay is its own window, so it only learns about the
          // toggle through this event
          applyField(
            'hummingbirdScreenshotFallbackEnabled',
            (raw) => raw === true
          );
        });
      } finally {
        this.isApplyingFromStorage = false;
      }
    };
    window.addEventListener('storage', applyStorageEvent);
    this.disposers.push(() =>
      window.removeEventListener('storage', applyStorageEvent)
    );
  }

  private initElectronReactions() {
    this.react(
      () => this.typeScale,
      (typeScale) => {
        electronApis.window.setWindowButtonPosition({
          x: WINDOW_BUTTON_POSITION_X,
          y:
            getWindowButtonPositionY(typeScale) +
            NAV_RAIL_TRAFFIC_LIGHT_OFFSET_Y,
        });
      },
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () =>
        config.meetings.status_window_option as ShowMeetingStatusWindowOption,
      (option) =>
        electronApis.meetings.setShowMeetingStatusWindowOption(option),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.privacy.hide_meeting_widget_from_screen_share,
      (hidden) =>
        electronApis.meetings.setHideMeetingWidgetFromScreenShare(hidden),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.meetings.notification_filter,
      (filter) => electronApis.meetings.setMeetingNotificationFilter(filter),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.meetings.reminder_lead_time,
      (leadTime) => electronApis.meetings.setMeetingReminderLeadTime(leadTime),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.meetings.disable_reminders,
      (disabled) => electronApis.meetings.setDisableMeetingReminders(disabled),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.meetings.exclude_solo_events,
      (exclude) => electronApis.meetings.setExcludeSoloEvents(exclude),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => ({
        daysAhead: config.calendar.days_ahead,
        maxEvents: config.calendar.max_events,
        excludedEventTypes:
          config.calendar.excluded_event_types.slice() as EventType[],
        excludedStatuses: config.calendar.excluded_statuses.slice(),
      }),
      (filters) => electronApis.meetings.setCalendarEventFilters(filters),
      { fireImmediately: true }
    );

    // Main-process defaults must be overwritten even when settings do not change this session
    this.reactToUserConfig(
      () => config.menubar.calendar_enabled,
      (enabled) => electronApis.meetings.setMenubarCalendarEnabled(enabled),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.menubar.include_events as MeetingMenubarIncludeEventsOption,
      (option) => electronApis.meetings.setMenubarIncludeEvents(option),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () =>
        config.menubar.preview_lead_time as MeetingMenubarPreviewLeadTimeOption,
      (option) => electronApis.meetings.setMenubarPreviewLeadTime(option),
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.meetings.disable_detection_notification,
      (disabled) =>
        electronApis.meetings.setDisableMeetingDetectionNotification(disabled),
      { fireImmediately: true }
    );

    this.react(
      () => this.disabledMeetingAppBundleKeys.slice(),
      (keys) => electronApis.meetings.setMeetingDetectionDisabledApps(keys),
      { fireImmediately: true }
    );

    this.react(
      () => this.disabledMeetingSiteHosts.slice(),
      (hosts) => electronApis.meetings.setMeetingDetectionDisabledSites(hosts),
      { fireImmediately: true }
    );

    // Main-process defaults must be overwritten even when settings do not change this session
    this.reactToUserConfig(
      () => config.notifications.sound_enabled,
      (enabled: boolean) => {
        electronApis.lbNotifications.setVolume(
          enabled ? DEFAULT_NOTIFICATION_VOLUME : 0
        );
      },
      { fireImmediately: true }
    );

    this.reactToUserConfig(
      () => config.notifications.sound,
      (sound: string) => {
        electronApis.lbNotifications.setSoundChoice(
          isNotificationSoundChoice(sound) ? sound : DEFAULT_SOUND_CHOICE
        );
      },
      { fireImmediately: true }
    );

    this.react(
      () => this.lbNotificationsEnabled,
      (enabled: boolean) => {
        electronApis.lbNotifications.setEnabled(enabled);
      }
    );

    this.react(
      () => this.appVisibilityMode,
      (mode) => electronApis.window.setAppVisibilityMode(mode)
    );

    this.react(
      () => this.hummingbirdEnabled,
      (enabled) => electronApis.hummingbird.setHummingbirdEnabled(enabled)
    );

    this.react(
      () => this.hummingbirdShortcut,
      (shortcut) => electronApis.hummingbird.setHummingbirdShortcut(shortcut)
    );

    this.react(
      () => this.voiceShortcutEnabled,
      (enabled) => electronApis.hummingbird.setVoiceShortcutEnabled(enabled)
    );

    this.react(
      () => this.voiceAutoSendEnabled,
      (enabled) => electronApis.hummingbird.setVoiceAutoSendEnabled(enabled)
    );

    this.react(
      () => this.hummingbirdHideFromScreenShare,
      (hidden) => electronApis.hummingbird.setHideFromScreenShare(hidden)
    );

    this.react(
      () => this.dictationEnabled,
      (enabled) => electronApis.dictation.setEnabled(enabled)
    );

    this.react(
      () => this.dictationHoldShortcut,
      (shortcut) => electronApis.dictation.setHoldShortcut(shortcut)
    );

    this.react(
      () => this.dictationShortcut,
      (shortcut) => electronApis.dictation.setShortcut(shortcut)
    );

    this.react(
      () => this.preferredDictationInputDevice,
      (selection) => electronApis.dictation.setInputDevice(selection),
      { equals: comparer.structural }
    );

    this.react(
      () => this.dictationAICleanupEnabled,
      (enabled) => electronApis.dictation.setAICleanupEnabled(enabled)
    );
  }

  private reactToUserConfig: typeof reaction = (...args) => {
    const dispose = when(
      () => hasSDK() && getSDK().isServicesInitialized,
      () => this.react(...(args as Parameters<typeof reaction>))
    );
    this.disposers.push(dispose);
    return dispose;
  };

  private initFromElectron() {
    if (!inElectron()) return;

    const subscriptions = [
      WindowAPI.event.on(MAIN_WINDOW_ENTER_FULLSCREEN, () => {
        this.setIsMainWindowFullScreen(true);
      }),
      WindowAPI.event.on(MAIN_WINDOW_LEAVE_FULLSCREEN, () => {
        this.setIsMainWindowFullScreen(false);
      }),
    ];
    // A reply that lands after stop() must not change a stopped store
    const hydration = new AbortController();
    this.disposers.push(() => {
      for (const subscription of subscriptions) subscription.unsubscribe();
      hydration.abort();
    });

    this.hydrateFromElectron(hydration.signal).catch((error) => {
      console.warn('Settings could not be read from the main process', error);
    });
  }

  /** The main process owns the hotkeys and notification switches; the device blob is only a cache of them */
  private async hydrateFromElectron(signal: AbortSignal): Promise<void> {
    const [
      isFullScreen,
      hbEnabled,
      hbShortcut,
      voiceEnabled,
      voiceAutoSend,
      hbHideFromScreenShare,
      dictationEnabled,
      dictationShortcut,
      meetingWidgetShortcut,
      dictationHoldShortcut,
      dictationInputDevice,
      dictationAICleanupEnabled,
      notificationsEnabled,
      timeFormatPref,
    ] = await Promise.all([
      electronApis.window.getMainWindowFullScreenState(),
      electronApis.hummingbird.getHummingbirdEnabled(),
      electronApis.hummingbird.getHummingbirdShortcut(),
      electronApis.hummingbird.getVoiceShortcutEnabled(),
      electronApis.hummingbird.getVoiceAutoSendEnabled(),
      electronApis.hummingbird.getHideFromScreenShare(),
      electronApis.dictation.getEnabled(),
      electronApis.dictation.getShortcut(),
      electronApis.meetings.getMeetingWidgetShortcut(),
      electronApis.dictation.getHoldShortcut(),
      electronApis.dictation.getInputDevice(),
      electronApis.dictation.getAICleanupEnabled(),
      electronApis.lbNotifications.getEnabled(),
      this.timeFormat === null
        ? electronApis.system.getPrefer12HourTime()
        : Promise.resolve(undefined),
    ]);

    if (signal.aborted) return;
    // A missing answer keeps the device blob's value instead of clearing it
    const apply = <T>(value: T | undefined, set: (value: T) => void) => {
      if (value !== undefined) set(value);
    };
    runInAction(() => {
      apply(isFullScreen, (value) => (this.isMainWindowFullScreen = value));
      apply(hbEnabled, (value) => (this.hummingbirdEnabled = value));
      apply(hbShortcut, (value) => (this.hummingbirdShortcut = value));
      apply(voiceEnabled, (value) => (this.voiceShortcutEnabled = value));
      apply(voiceAutoSend, (value) => (this.voiceAutoSendEnabled = value));
      apply(
        hbHideFromScreenShare,
        (value) => (this.hummingbirdHideFromScreenShare = value)
      );
      apply(dictationEnabled, (value) => (this.dictationEnabled = value));
      apply(dictationShortcut, (value) => (this.dictationShortcut = value));
      apply(
        meetingWidgetShortcut,
        (value) => (this.meetingWidgetShortcut = value)
      );
      apply(
        dictationHoldShortcut,
        (value) => (this.dictationHoldShortcut = value)
      );
      apply(
        dictationInputDevice,
        (value) =>
          (this.preferredDictationInputDevice =
            normalizePreferredInputDevice(value))
      );
      apply(
        dictationAICleanupEnabled,
        (value) => (this.dictationAICleanupEnabled = value)
      );
      apply(
        notificationsEnabled,
        (value) => (this.lbNotificationsEnabled = value)
      );
      apply(timeFormatPref, (value) => (this.timeFormat = value));
    });
  }
}

export const settingsStore = new SettingsStore();
// Every window starts the reactions when this module loads, as before start()
// existed; a test stops them with the disposer and starts its own instance
settingsStore.start();
